import { DashboardTile } from "../components/DashboardTile";
import { TileHeader } from "../components/TileHeader";
import { useMemo } from "react";
import { useTransactionsQuery } from "@common/utils/graphql";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatDate } from "@common/components/utils";
import { Body } from "@hypercard/web-components";
import { IconBox } from "@common/components";
import { CreditCard } from "@phosphor-icons/react";
import {
  ListItemProps,
  getTransactionColor,
  isValidTransactionType,
} from "@/utils/utils";
import { twMerge } from "@/utils/utils";

type TransactionListItemProps = {
  transaction: ListItemProps;
};
const TransactionListItem = ({ transaction }: TransactionListItemProps) => {
  const colorType = getTransactionColor(transaction);

  const color =
    colorType === "green"
      ? "#54C186"
      : colorType === "red"
        ? "#BE3E22"
        : undefined;

  const background = colorType
    ? `linear-gradient(90deg, ${color}00 0%, ${color}24 11.05%, ${color}1F 26.56%, ${color}00 100%)`
    : undefined;

  const filter =
    colorType === "red"
      ? "brightness(0) saturate(100%) invert(23%) sepia(75%) saturate(1545%) hue-rotate(346deg) brightness(103%) contrast(90%)"
      : undefined;

  return (
    <div className="flex items-center p-1" style={{ background }}>
      <IconBox className="mr-4 shrink-0">
        <img
          className="size-5"
          style={{ filter }}
          src={transaction.hypercardCategoryIconUrl}
        />
      </IconBox>
      <div className="mb-1 flex grow flex-col justify-between overflow-hidden">
        <div className="flex justify-between">
          <Tooltip>
            <TooltipTrigger className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left text-sm">
              {transaction.displayTitle}
            </TooltipTrigger>
            <TooltipContent>
              <Body size="sm" weight="medium">
                {transaction.displayTitle}
              </Body>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
          <p className="text-sm font-medium leading-[1.2]" style={{ color }}>
            {transaction.displayDollarAmount}
          </p>
        </div>
        <div className="flex justify-between">
          <Body size="xs" weight="regular" className="text-white/70">
            {formatDate(transaction.transactedAt)}
          </Body>
          <Body size="xs" weight="regular" className="text-white/50">
            {transaction.displayFlair}
          </Body>
        </div>
      </div>
    </div>
  );
};

type Props = {
  className?: string;
};

export const LastTransactionsTile = ({ className }: Props) => {
  const {
    loading: transactionsLoading,
    data: transactionsData,
    error,
  } = useTransactionsQuery();

  const transactions = useMemo(() => {
    if (
      transactionsData?.transactions?.__typename !== "QueryTransactionsSuccess"
    ) {
      return [];
    }
    return transactionsData?.transactions?.data.allTransactions.slice(0, 5);
  }, [transactionsData]);

  const emptyTile = (
    <div className="flex h-full flex-col items-center justify-center pb-8">
      <IconBox>
        <CreditCard className="size-8" />
      </IconBox>
      <Body size="lg" weight="medium" className="mt-6">
        Instant Reimbursements and 3X Points
      </Body>
      <Body
        size="sm"
        weight="regular"
        className="mt-2 whitespace-pre text-center text-white/70"
      >
        {"Get instantly reimbursed on your company expenses."}
      </Body>
    </div>
  );
  return (
    <DashboardTile
      className={twMerge(
        "flex flex-col justify-between overflow-hidden",
        className,
      )}
    >
      <TileHeader>LAST TRANSACTIONS</TileHeader>
      {error ? (
        emptyTile
      ) : (
        <div className="flex h-full flex-col gap-2 overflow-auto pt-1 scrollbar-thin">
          {transactionsLoading ? (
            <Skeleton className="mt-5 h-[250px] w-full rounded-xl" />
          ) : transactions.length > 0 ? (
            transactions
              .filter(isValidTransactionType)
              .map((val, idx) => (
                <TransactionListItem
                  key={`transactions--list-item-${val.transactedAt}-${idx}`}
                  transaction={val}
                />
              ))
          ) : (
            emptyTile
          )}
        </div>
      )}
    </DashboardTile>
  );
};
